import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppState } from '@/store';
import {
  contentPagesListSelector,
  selectBlogCategoryOptions,
  setListBlogCategoryFilter,
  setListSearch,
  setListSearchAuthor,
  setListSearchId,
} from '@/modules/ContentPages';
import { SupportedLocales } from '@/models/localization.model';
import { Input } from '@/ui';

import { StyledFilterRow, StyledFilterSelect } from './ContentPagesPostsFiltersContainerStyles';

type TContentPagesPostsFiltersContainerProps = {
  children?: ReactNode;
  className?: string;
};

const ContentPagesPostsFiltersContainer = ({ children, className }: TContentPagesPostsFiltersContainerProps) => {
  const { t, i18n } = useTranslation(['common']);
  const dispatch = useAppDispatch();
  const { blogCategoryId, search, searchId, searchAuthor } = useAppState(contentPagesListSelector);
  const activeLocale = i18n.language === SupportedLocales.RU ? SupportedLocales.RU : SupportedLocales.UK;
  const categoryOptions = useAppState(selectBlogCategoryOptions(activeLocale));

  const [idDraft, setIdDraft] = useState(searchId);
  const [authorDraft, setAuthorDraft] = useState(searchAuthor);
  const [titleDraft, setTitleDraft] = useState(search);

  useEffect(() => {
    setIdDraft(searchId);
  }, [searchId]);

  useEffect(() => {
    setAuthorDraft(searchAuthor);
  }, [searchAuthor]);

  useEffect(() => {
    setTitleDraft(search);
  }, [search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (idDraft !== searchId) {
        dispatch(setListSearchId(idDraft));
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [dispatch, idDraft, searchId]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (authorDraft !== searchAuthor) {
        dispatch(setListSearchAuthor(authorDraft));
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [authorDraft, dispatch, searchAuthor]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (titleDraft !== search) {
        dispatch(setListSearch(titleDraft));
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [dispatch, search, titleDraft]);

  const handleIdChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIdDraft(e.target.value);
  };

  const handleAuthorChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAuthorDraft(e.target.value);
  };

  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTitleDraft(e.target.value);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <Input
            allowClear
            placeholder={t('content_pages_list_search_id_placeholder')}
            size="large"
            value={idDraft}
            onChange={handleIdChange}
          />
        </th>
        <th>
          <StyledFilterSelect
            allowClear
            options={categoryOptions}
            placeholder={t('content_pages_list_filter_category_all')}
            value={blogCategoryId ?? undefined}
            onChange={(value) =>
              dispatch(setListBlogCategoryFilter(value != null && value !== '' ? String(value) : null))
            }
            size="large"
          />
        </th>
        <th />
        <th>
          <Input
            allowClear
            placeholder={t('content_pages_list_search_author_placeholder')}
            size="large"
            value={authorDraft}
            onChange={handleAuthorChange}
          />
        </th>
        <th>
          <Input
            allowClear
            placeholder={t('content_pages_list_search_title_placeholder')}
            size="large"
            value={titleDraft}
            onChange={handleTitleChange}
          />
        </th>
        <th />
        <th />
        <th />
      </StyledFilterRow>
    </thead>
  );
};

export default ContentPagesPostsFiltersContainer;
